import React, { useCallback } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import SafeIcon from '../common/SafeIcon';

const { 
  FiPlay, FiEye, FiUsers, FiZap, FiTrendingUp, FiHeadphones, FiLightbulb, 
  FiGrid, FiImage, FiTag, FiMessageCircle, FiVideo, FiShare2, FiPalette,
  FiExternalLink, FiShoppingBag, FiInstagram, FiTwitter, FiLinkedin,
  FiYoutube, FiMusic, FiMail, FiClock, FiStar, FiArrowRight
} = FiIcons;

const { FaTiktok, FaWhatsapp, FaSpotify, FaApple, FaAmazon, FaCoffee } = FaIcons;

const Home = () => {
  const handleAccessGPT5 = useCallback(() => {
    window.open('https://app.gpt5.au', '_blank', 'noopener,noreferrer');
  }, []);

  const handleWhatsAppGroup = useCallback(() => {
    window.open('https://chat.whatsapp.com/Kluk7dTnR6t2cQ8YKazD0m', '_blank', 'noopener,noreferrer');
  }, []);

  const handleMerchShop = useCallback(() => {
    window.open('https://shop.dadda.cool', '_blank', 'noopener,noreferrer');
  }, []);

  const handleSocialLink = useCallback((url) => () => {
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const aiTools = [
    {
      title: 'AI Insights',
      description: 'Stay updated with the latest AI news, trends, and analysis. Get daily insights on breakthrough technologies.',
      icon: FiTrendingUp,
      color: 'bg-neo-primary'
    },
    {
      title: 'AI Inspiration',
      description: 'Discover creative AI prompts paired with stunning visuals. Browse curated examples with exact prompts.',
      icon: FiLightbulb,
      color: 'bg-neo-secondary'
    },
    {
      title: '69 AI Tools',
      description: 'Explore the most comprehensive collection of 69 AI tools. Curated, categorized, and ready to supercharge your workflow.',
      icon: FiGrid,
      color: 'bg-neo-accent'
    },
    {
      title: 'AI Audio Books',
      description: 'Listen to lessons from popular books voiced by Dadda Cool. Curated audio content that brings knowledge to life.',
      icon: FiHeadphones,
      color: 'bg-neo-primary'
    },
    {
      title: 'AI Idea Generator',
      description: 'Generate trending, creative content ideas based on your niche, genre, and keywords using AI trend analysis.',
      icon: FiLightbulb,
      color: 'bg-neo-secondary'
    },
    {
      title: 'Storyboard Generator',
      description: 'Transform your scripts into comprehensive storyboards with up to 32 scenes. AI breaks down content scene-by-scene.',
      icon: FiImage,
      color: 'bg-neo-accent'
    },
    {
      title: 'AI Thumbnail Downloader',
      description: 'Download YouTube thumbnails in HD quality and generate AI-powered thumbnail design prompts for inspiration.',
      icon: FiImage,
      color: 'bg-neo-primary'
    },
    {
      title: 'Tags Generator',
      description: 'Generate SEO-optimized tags for YouTube and trending hashtags for TikTok. Boost content discoverability.',
      icon: FiTag,
      color: 'bg-neo-secondary'
    },
    {
      title: 'ThreadGPT',
      description: 'Generate viral Twitter threads with AI. Transform ideas into engaging, human-sounding threads that drive engagement.',
      icon: FiMessageCircle,
      color: 'bg-neo-accent'
    },
    {
      title: 'ScriptGPT',
      description: 'Generate viral scripts for TikTok, Reels, and Shorts using the proven S.T.A.R.T. framework for scroll-stopping content.',
      icon: FiVideo,
      color: 'bg-neo-primary'
    },
    {
      title: 'AI Message Generator',
      description: 'Turn a single idea into perfectly crafted posts for LinkedIn, Facebook, Twitter, Reddit, Discord, and WhatsApp.',
      icon: FiShare2,
      color: 'bg-neo-secondary'
    },
    {
      title: 'Sora 2 Prompts',
      description: 'Generate cinematic Sora 2 video prompts in JSON format. Transform simple ideas into production-ready prompts.',
      icon: FiVideo,
      color: 'bg-neo-accent'
    },
    {
      title: 'Go Viral',
      description: 'Generate complete social media packages for YouTube, TikTok, Instagram, and Facebook with optimized content.',
      icon: FiTrendingUp,
      color: 'bg-neo-primary'
    },
    {
      title: 'Art Style Prompts',
      description: 'Generate professional AI art prompts with 25+ artistic styles. From watercolor to cyberpunk for any art generator.',
      icon: FiPalette,
      color: 'bg-neo-secondary'
    }
  ];

  const features = [
    { title: 'Lightning Fast', description: 'Generate complete storyboards in minutes, not hours', icon: FiZap },
    { title: 'Multiple Styles', description: 'Choose from 10+ visual styles including Cinematic, Anime, Ghibli, and more', icon: FiPalette },
    { title: 'Prompts Generator', description: 'Professional-grade prompts optimized for high-quality output', icon: FiStar },
    { title: 'Save Time', description: 'Automate your content planning and focus on what matters - creating', icon: FiClock }
  ];

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-neo-primary border-b-4 border-neo-border">
        <nav className="container mx-auto px-4 md:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="text-2xl font-bold text-neo-text">GPT5 Hub</div>
            <div className="flex items-center gap-4">
              <button onClick={handleAccessGPT5} className="neo-button bg-neo-secondary text-white">
                Access GPT5 Hub
              </button>
              <button onClick={handleWhatsAppGroup} className="neo-button bg-neo-accent">
                <SafeIcon icon={FaWhatsapp} className="inline w-5 h-5 mr-2" />
                Join WhatsApp
              </button>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-4 md:px-6 text-center">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="neo-card bg-neo-primary p-8 md:p-16 mb-8"
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-6">GPT5 Hub</h1>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">AI Content Studio</h2>
            <p className="text-xl md:text-2xl mb-8 max-w-4xl mx-auto">
              Your all-in-one AI-powered platform for creating professional video content. Generate ideas, build storyboards, and design thumbnails - all in one place.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={handleAccessGPT5} className="neo-button bg-neo-secondary text-white text-xl">
                See What Happens
              </button>
              <button onClick={handleAccessGPT5} className="neo-button bg-neo-accent text-xl">
                <SafeIcon icon={FiEye} className="inline w-6 h-6 mr-2" />
                View History
              </button>
              <button onClick={handleWhatsAppGroup} className="neo-button bg-white text-xl">
                <SafeIcon icon={FaWhatsapp} className="inline w-6 h-6 mr-2" />
                Join WhatsApp Group
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* AI Tools Section */}
      <section className="py-16 px-4 md:px-6 bg-neo-accent border-y-4 border-neo-border">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Powerful AI Tools for Content Creators</h2>
            <button onClick={handleAccessGPT5} className="neo-button bg-neo-primary text-xl">
              <SafeIcon icon={FiExternalLink} className="inline w-6 h-6 mr-2" />
              Access All Tools at GPT5 Hub
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {aiTools.map((tool, index) => (
              <motion.div
                key={tool.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="neo-card bg-white p-6"
              >
                <div className={`${tool.color} w-16 h-16 rounded-full flex items-center justify-center mb-4 border-3 border-neo-border`}>
                  <SafeIcon icon={tool.icon} className="w-8 h-8 text-neo-text" />
                </div>
                <h3 className="text-2xl font-bold mb-3">{tool.title}</h3>
                <p className="text-base mb-4">{tool.description}</p>
                <button onClick={handleAccessGPT5} className="neo-button bg-neo-primary w-full">
                  See What Happens
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Why Choose GPT5 Hub?</h2>
            <button onClick={handleAccessGPT5} className="neo-button bg-neo-secondary text-white text-xl">
              Experience GPT5 Hub Now
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="neo-card bg-white p-6 text-center"
              >
                <div className="bg-neo-primary w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 border-3 border-neo-border">
                  <SafeIcon icon={feature.icon} className="w-8 h-8 text-neo-text" />
                </div>
                <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                <p className="text-base">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Merch Promo Section */}
      <section className="py-16 px-4 md:px-6 bg-neo-secondary border-y-4 border-neo-border">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-white"
          >
            <SafeIcon icon={FiShoppingBag} className="w-20 h-20 mx-auto mb-6" />
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Shop Dadda Cool Merch</h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Get exclusive merchandise and show your support for the AI revolution!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={handleMerchShop} className="neo-button bg-neo-primary text-xl">
                <SafeIcon icon={FiShoppingBag} className="inline w-6 h-6 mr-2" />
                Shop Merch Now
              </button>
              <button onClick={handleAccessGPT5} className="neo-button bg-neo-accent text-xl">
                <SafeIcon icon={FiArrowRight} className="inline w-6 h-6 mr-2" />
                Try GPT5 Hub
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 px-4 md:px-6 bg-neo-primary border-y-4 border-neo-border">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Create Amazing Content?</h2>
            <p className="text-xl mb-8">Join thousands of creators using GPT5 Hub to supercharge their content creation.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={handleAccessGPT5} className="neo-button bg-neo-secondary text-white text-2xl px-12 py-6">
                See What Happens
              </button>
              <button onClick={handleWhatsAppGroup} className="neo-button bg-neo-accent text-2xl px-12 py-6">
                <SafeIcon icon={FaWhatsapp} className="inline w-8 h-8 mr-3" />
                Join WhatsApp Group
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Simplified Footer */}
      <footer className="py-8 px-4 md:px-6 bg-neo-bg border-t-4 border-neo-border">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-lg font-bold mb-4">© 2025 Ai8848. All rights reserved.</p>
          <button onClick={handleAccessGPT5} className="neo-button bg-neo-primary">
            <SafeIcon icon={FiExternalLink} className="inline w-5 h-5 mr-2" />
            Launch GPT5 Hub Now
          </button>
        </div>
      </footer>
    </div>
  );
};

export default Home;